import React, { useEffect, useRef, useState } from "react";
import MuxPlayer from "@mux/mux-player-react";
import api, { fileUrl } from "../lib/api";

/**
 * Smart video renderer for posts.
 *  - Lazy: the heavy player only mounts when the post is near the viewport
 *    (IntersectionObserver, 150% rootMargin). Offscreen posts show a Mux
 *    poster thumbnail instead — faster scrolling, less memory & bandwidth.
 *  - Mux playback_id → MuxPlayer (HLS, adaptive bitrate, CDN).
 *  - Mux upload processing → poll status with a spinner.
 *  - Legacy posts → plain <video preload="metadata">.
 */
export default function PostMedia({ post, fallbackImage }) {
  const [playbackId, setPlaybackId] = useState(post.mux_playback_id || null);
  const [muxStatus, setMuxStatus] = useState(post.mux_status || null);
  const [inView, setInView] = useState(false);
  const pollRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: "150% 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Analytics: count a view once per mount + accumulate watch time while visible
  const viewSentRef = useRef(false);
  const watchStartRef = useRef(null);
  useEffect(() => {
    if (post.media_type !== "video") return;
    const flush = () => {
      const start = watchStartRef.current;
      if (!start) return;
      watchStartRef.current = null;
      const secs = Math.min(600, Math.round((Date.now() - start) / 1000));
      if (secs >= 1) api.post(`/posts/${post.id}/watch`, { seconds: secs }).catch(() => {});
    };
    if (inView) {
      if (!viewSentRef.current) {
        viewSentRef.current = true;
        api.post(`/posts/${post.id}/view`).catch(() => {});
      }
      watchStartRef.current = Date.now();
    } else {
      flush();
    }
    return flush;
  }, [inView, post.id, post.media_type]);

  // Poll Mux upload status for processing videos
  useEffect(() => {
    if (post.media_type !== "video") return;
    if (playbackId) return; // already ready
    if (!post.mux_upload_id) return; // not a Mux video
    if (muxStatus === "ready") return;

    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/mux/upload-status/${post.mux_upload_id}`);
        if (cancelled) return;
        if (data.mux_playback_id) setPlaybackId(data.mux_playback_id);
        if (data.status) setMuxStatus(data.status);
        if (data.status !== "ready" && data.status !== "errored") {
          pollRef.current = setTimeout(poll, 6000);
        }
      } catch {
        if (!cancelled) pollRef.current = setTimeout(poll, 12000);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [post.mux_upload_id, post.media_type, playbackId, muxStatus]);

  if (post.media_type !== "video") {
    return (
      <img
        src={post.media_path ? fileUrl(post.media_path) : fallbackImage}
        alt={post.caption || "post"}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
    );
  }

  const subtitleUrl = post.subtitles_vtt || post.id
    ? `${process.env.REACT_APP_BACKEND_URL}/api/posts/${post.id}/subtitles.vtt`
    : null;

  return (
    <div ref={containerRef} className="absolute inset-0" data-testid={`post-media-${post.id}`}>
      {playbackId ? (
        inView ? (
          <MuxPlayer
            streamType="on-demand"
            playbackId={playbackId}
            autoPlay="muted"
            loop
            muted
            playsInline
            nohotkeys
            thumbnailTime={0}
            metadata={{
              video_id: post.id,
              video_title: post.caption?.slice(0, 80) || "Untitled",
            }}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              "--controls": "none",
              "--media-object-fit": "cover",
            }}
          >
            {subtitleUrl && (
              <track
                kind="subtitles"
                label="English"
                srcLang="en"
                src={subtitleUrl}
                default
              />
            )}
          </MuxPlayer>
        ) : (
          <img
            src={`https://image.mux.com/${playbackId}/thumbnail.webp?time=0&width=720`}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )
      ) : post.mux_upload_id && muxStatus !== "ready" ? (
        <div className="absolute inset-0 bg-ink flex flex-col items-center justify-center text-cream/60">
          <div className="w-12 h-12 border-2 border-papaya border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-papaya">
            {muxStatus === "errored" ? "Upload failed" : "Encoding video…"}
          </p>
        </div>
      ) : inView ? (
        <video
          src={post.media_path ? fileUrl(post.media_path) : ""}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-ink" />
      )}
    </div>
  );
}
